import { Section } from '@/components/primitives/Section';
import { Button } from '@/components/ui/button';
import { DollarSign, MapPin, Calendar, TrendingUp, Zap, Shield, Star } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { motion } from 'framer-motion';

export function OperatorsSection() {
  return (
    <Section
      id="operators"
      eyebrow="For Snow Removal Operators"
      title="Turn your equipment into a revenue machine"
      subtitle="Own a snowblower, plow, or even just a shovel? Get matched with high-paying jobs in your neighborhood. Work when you want. Keep 75-85% of every dollar."
      background="dark"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start mt-16">
        {/* Left: Benefits - 3 columns */}
        <div className="lg:col-span-3 space-y-6">
          <motion.div 
            className="flex items-start gap-4 p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-accent-500/50 transition-all group"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center flex-shrink-0 shadow-glow-green">
              <DollarSign className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="font-black text-2xl text-white mb-2 group-hover:text-accent-500 transition-colors">Earn 75–85% per job</h3>
              <p className="text-neutral-300 font-medium leading-relaxed">
                Keep the lion's share. No monthly fees, no commission caps, no hidden deductions. 
                <span className="text-accent-500 font-bold"> Just straight cash to your account.</span>
              </p>
            </div>
          </motion.div>

          <motion.div 
            className="flex items-start gap-4 p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-brand-500/50 transition-all group"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center flex-shrink-0 shadow-glow">
              <MapPin className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="font-black text-2xl text-white mb-2 group-hover:text-brand-500 transition-colors">Total control of your schedule</h3>
              <p className="text-neutral-300 font-medium leading-relaxed">
                Set your radius, availability, and equipment type. Toggle online/offline anytime. 
                <span className="text-brand-400 font-bold"> Accept only the jobs you want.</span>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-white/10 text-white rounded-lg text-xs font-bold">Choose your hours</span>
                <span className="px-3 py-1 bg-white/10 text-white rounded-lg text-xs font-bold">Set your area</span>
                <span className="px-3 py-1 bg-white/10 text-white rounded-lg text-xs font-bold">Pick your jobs</span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="flex items-start gap-4 p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-accent-500/50 transition-all group"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-neutral-700 to-neutral-900 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-7 h-7 text-accent-500" strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="font-black text-2xl text-white mb-2 group-hover:text-accent-500 transition-colors">Get paid fast — really fast</h3>
              <p className="text-neutral-300 font-medium leading-relaxed">
                Automatic weekly deposits to your bank. Need it faster? 
                <span className="text-accent-500 font-bold"> Instant payout available for $1.99.</span> Your money, your timeline.
              </p>
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-accent-500/20 border border-accent-500/30 rounded-xl">
                <Zap className="w-4 h-4 text-accent-500" fill="currentColor" />
                <span className="text-accent-500 font-bold text-sm">Cash out in 30 seconds</span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="flex gap-4 pt-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Button 
              variant="primary" 
              size="lg"
              className="group bg-accent-500 hover:bg-accent-600 shadow-glow-green text-lg font-black px-8 py-6"
              onClick={() => {
                trackEvent('operator_apply_click');
                window.location.href = '/operators';
              }}
            >
              <span className="flex items-center gap-2">
                Start Earning Today
                <motion.span
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  →
                </motion.span>
              </span>
            </Button>
            <Button 
              variant="secondary" 
              size="lg"
              className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 text-lg font-bold px-8 py-6"
              onClick={() => {
                trackEvent('operator_app_preview_click');
                window.location.href = '/operators#app';
              }}
            >
              See How It Works
            </Button>
          </motion.div>
        </div>

        {/* Right: Stats Card - 2 columns */}
        <motion.div 
          className="lg:col-span-2"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="sticky top-8 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-card">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-accent-500 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-2xl font-black text-white">This Week's</h3>
                <p className="text-accent-500 font-bold uppercase text-sm tracking-wider">Top Earners</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <motion.div 
                className="flex items-center justify-between p-5 bg-gradient-to-r from-accent-500/20 to-transparent rounded-2xl border-l-4 border-accent-500"
                whileHover={{ x: 4 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent-500 flex items-center justify-center font-black text-white text-xl">
                    1
                  </div>
                  <div>
                    <p className="font-black text-white text-lg">Mike D.</p>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-accent-500" fill="currentColor" />
                      <p className="text-sm text-neutral-400 font-medium">18 jobs • 4.9★</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black text-accent-500">$678</p>
                  <p className="text-xs text-neutral-400 font-bold uppercase">Earned</p>
                </div>
              </motion.div>

              <motion.div 
                className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/10"
                whileHover={{ x: 4 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-500 flex items-center justify-center font-black text-white text-xl">
                    2
                  </div>
                  <div>
                    <p className="font-black text-white text-lg">Sarah L.</p>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-accent-500" fill="currentColor" />
                      <p className="text-sm text-neutral-400 font-medium">14 jobs • 5.0★</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black text-white">$542</p>
                  <p className="text-xs text-neutral-400 font-bold uppercase">Earned</p>
                </div>
              </motion.div>

              <motion.div 
                className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/10"
                whileHover={{ x: 4 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-neutral-700 flex items-center justify-center font-black text-white text-xl">
                    3
                  </div>
                  <div>
                    <p className="font-black text-white text-lg">Tom R.</p>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-accent-500" fill="currentColor" />
                      <p className="text-sm text-neutral-400 font-medium">11 jobs • 4.8★</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black text-white">$455</p>
                  <p className="text-xs text-neutral-400 font-bold uppercase">Earned</p>
                </div>
              </motion.div>
            </div>

            <div className="mt-8 p-6 bg-brand-500/10 backdrop-blur-sm rounded-2xl border border-brand-500/20">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-brand-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white font-bold mb-1">"No flyers. No door-knocking."</p>
                  <p className="text-sm text-neutral-300 italic leading-relaxed">
                    "I went from chasing clients to having them delivered to my phone. Game changer."
                  </p>
                  <p className="text-xs text-brand-400 font-bold mt-2">— Jason M., Operator since 2024</p>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-neutral-400 font-medium">
                <span className="text-accent-500 font-black">423 operators</span> joined this month
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

